/**
 * 全画面共通のレイアウト。骨組みは共通UIの AppShell(設計書 §5-1)、ここでは cc-system のメニューだけを決める。
 * ロール別表示(§3): admin=全メニュー / viewer=日次・月次の閲覧 / operator=スタッフのみ。
 */
import { BUILD_LABEL } from '@core/lib/buildInfo';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import AppShell, { type ShellNavSection } from '@core/components/ui/AppShell';
import AppUpdateButton from '@core/components/AppUpdateButton';
import { appEnv, firestoreDbId, getProjectId } from '@core/lib/firebase';
import { ROLE_LABELS } from '@core/lib/types';
import { useStackedTables } from '@core/lib/stackedTables';
import { useTheme } from '@core/theme/ThemeProvider';
import { THEMES } from '@core/theme/themes';

type NavItem = { to: string; label: string; visible: boolean };

export const SCREEN_TITLES: Record<string, string> = {
  '/home': 'ダッシュボード',
  '/operators': 'オペレーターマスタ',
  '/companies': '会社マスタ',
  '/import': 'CSVインポート',
  '/bb-credentials': 'BB ID／パスワード一覧',
  '/pl': 'PL',
  '/summary/deliveries': '締め作業(納品・人件費・交通費)',
  '/close': '日次締め作業',
  '/close-month': '月締め作業',
  '/summary/companies': '会社別',
  '/summary/styles': '出勤形態×報酬区分別',
  '/summary/staff': '日次【集計】',
  '/summary/monthly-staff': '月次【スタッフ別】',
  '/analysis': '分析',
  '/staff': '本人成績',
  '/shift': 'シフト',
  '/qa': 'Q&A',
  '/training': 'トレーニング経過',
  '/settings/users': 'ユーザー管理',
  '/settings/monthly': '月次設定(目標・前提値)',
  '/settings/pay': '報酬・通話料の設定',
  '/settings/delivery-plan': '納品件数の予定(曜日別)',
  '/settings/system': 'システム設定',
  '/settings/bluebean-api': 'BlueBean API連携',
  '/design-preview': 'デザイン比較',
};

export default function AppLayout() {
  const { user, role, signOut, isAdmin, isViewer, isOperator } = useAuth();
  const { previewTheme } = useTheme();
  const { pathname } = useLocation();
  /** スマホでは表を縦積みカードにする(列の多い集計表の方式。一覧＋詳細型は DataTable のカード一覧: docs/design-system.md §6-2) */
  useStackedTables();

  /** 管理者のログイン直後の画面(2026-09-12 指示) */
  const topItems: NavItem[] = [{ to: '/home', label: 'ダッシュボード', visible: isAdmin }];
  const masterItems: NavItem[] = [
    { to: '/operators', label: 'オペレーターマスタ', visible: isAdmin || isViewer },
    { to: '/companies', label: '会社マスタ', visible: isAdmin || isViewer },
    { to: '/bb-credentials', label: 'BB ID／パスワード一覧', visible: isAdmin },
    { to: '/import', label: 'CSVインポート', visible: isAdmin },
  ];
  /** 集計(⑥指示): 各ページ内で日次/週次/月次を切り替える */
  const summaryItems: NavItem[] = [
    { to: '/pl', label: 'PL', visible: isAdmin || isViewer },
    // 2026-09-20 発注者指示でメニューから外す(画面は残してあるので、URL を直接開けば見られる)
    { to: '/summary/companies', label: '会社別', visible: false },
    { to: '/summary/styles', label: '出勤形態×報酬区分別', visible: false },
    // 日報/サマリ(旧 納品件数の入力)は日次【集計】の上(2026-09-10 指示)
    { to: '/close', label: '締め作業', visible: isAdmin },
    { to: '/close-month', label: '月締め作業', visible: isAdmin },
    { to: '/summary/deliveries', label: '締め作業(月の一覧)', visible: isAdmin },
    { to: '/summary/staff', label: '日次【集計】', visible: isAdmin || isViewer },
    { to: '/summary/monthly-staff', label: '月次【スタッフ別】', visible: isAdmin || isViewer },
    { to: '/analysis', label: '分析', visible: isAdmin || isViewer },
  ];
  const staffItems: NavItem[] = [
    { to: '/staff', label: '本人成績', visible: isAdmin || isOperator },
    { to: '/shift', label: 'シフト', visible: isAdmin || isOperator },
    { to: '/training', label: 'トレーニング経過', visible: isAdmin },
    { to: '/qa', label: 'Q&A', visible: isAdmin || isOperator },
  ];
  const settingItems: NavItem[] = [
    { to: '/settings/users', label: 'ユーザー管理', visible: isAdmin },
    { to: '/settings/monthly', label: '月次設定', visible: isAdmin },
    { to: '/settings/pay', label: '報酬・通話料', visible: isAdmin },
    { to: '/settings/delivery-plan', label: '納品件数の予定', visible: isAdmin },
    { to: '/settings/system', label: 'システム設定', visible: isAdmin },
    { to: '/settings/bluebean-api', label: 'BlueBean API連携', visible: isAdmin },
    // 4つのテーマの見比べ(D-132)。正式な切り替えは システム設定 → 外観設定
    { to: '/design-preview', label: 'デザイン比較', visible: isAdmin },
  ];

  const section = (label: string, items: NavItem[]): ShellNavSection => ({ label, items: items.filter((i) => i.visible).map(({ to, label: l }) => ({ to, label: l })) });
  // 並びは 2026-09-12 指示: ダッシュボード → 集計 → スタッフ → マスタ → 設定
  const nav = [section('ダッシュボード', topItems), section('集計', summaryItems), section('スタッフ', staffItems), section('マスタ', masterItems), section('設定', settingItems)];

  return (
    <AppShell
      brand={
        <>
          <div className="brand">SC NEXUS / CC DIVISION</div>
          <div className="side-title">cc-system</div>
        </>
      }
      nav={nav}
      title={SCREEN_TITLES[pathname] ?? 'cc-system'}
      actions={<AppUpdateButton />}
      user={
        <>
          {user?.name}
          <span className="user-role"> ・{role ? ROLE_LABELS[role] : ''}</span>
        </>
      }
      footer={
        <>
          <button className="side-signout" onClick={() => void signOut()}>
            ログアウト
          </button>
          {/* バージョン・ビルド時刻・コミットID。どの版が動いているかの確認用(2026-09-10 指示。2026-10-08 コミットIDを足して画面下部へ) */}
          <div className="side-version">
            v{__APP_VERSION__} <span>{BUILD_LABEL}</span>
          </div>
        </>
      }
      banner={
        <>
          {/* テスト環境の帯。手元の開発サーバーと、テスト用データベースに繋いだビルドで出す(D-65) */}
          {(firestoreDbId !== '(default)' || (import.meta.env.DEV && appEnv === 'dev')) && (
            <div className="envband">
              テスト環境({getProjectId()}
              {firestoreDbId !== '(default)' ? ` / データベース ${firestoreDbId}` : ''})に接続しています — 本番データではありません
            </div>
          )}
          {/* デザイン比較でテーマを一時的に見ているあいだ(D-132)。保存していないことを常に見せる */}
          {previewTheme && (
            <div className="preview-band" role="status">
              デザインのプレビュー中: 「{THEMES[previewTheme].label}」を一時的に表示しています(保存されません。この画面を離れると元に戻ります)
            </div>
          )}
        </>
      }
      sideHiddenStorageKey="cc.sideHidden"
    >
      <Outlet />
    </AppShell>
  );
}
